/**
 * Trích xuất màu chủ đạo từ hình ảnh bằng HTML5 Canvas
 * @param {string} imageUrl - URL hoặc Blob URL của ảnh
 * @param {string} defaultColor - Mã màu mặc định nếu không đọc được
 * @returns {Promise<string>} Mã màu HEX
 */
export const extractDominantColor = (imageUrl, defaultColor = '#009A97') =>
  new Promise((resolve) => {
    if (!imageUrl) {
      resolve(defaultColor)
      return
    }

    const img = new Image()
    img.crossOrigin = 'Anonymous'

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d', { willReadFrequently: true })
        if (!ctx) {
          resolve(defaultColor)
          return
        }

        // Scale nhỏ về 40x40 px để đọc nhanh và gom màu trung bình
        const size = 40
        canvas.width = size
        canvas.height = size

        ctx.drawImage(img, 0, 0, size, size)
        const { data } = ctx.getImageData(0, 0, size, size)

        let totalR = 0
        let totalG = 0
        let totalB = 0
        let totalWeight = 0

        let fallbackR = 0
        let fallbackG = 0
        let fallbackB = 0
        let fallbackCount = 0

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i]
          const g = data[i + 1]
          const b = data[i + 2]
          const a = data[i + 3]

          if (a >= 128) {
            fallbackR += r
            fallbackG += g
            fallbackB += b
            fallbackCount += 1

            const max = Math.max(r, g, b)
            const min = Math.min(r, g, b)
            const delta = max - min
            const saturation = max === 0 ? 0 : delta / max
            const brightness = (r * 299 + g * 587 + b * 114) / 1000

            // Lọc bớt pixel quá sáng (gần trắng) hoặc quá tối (gần đen) để giữ màu chủ đạo
            if (brightness <= 240 && brightness >= 20) {
              const weight = saturation * saturation + 0.1
              totalR += r * weight
              totalG += g * weight
              totalB += b * weight
              totalWeight += weight
            }
          }
        }

        let finalR = 0
        let finalG = 0
        let finalB = 0

        if (totalWeight > 0) {
          finalR = Math.round(totalR / totalWeight)
          finalG = Math.round(totalG / totalWeight)
          finalB = Math.round(totalB / totalWeight)
        } else if (fallbackCount > 0) {
          finalR = Math.round(fallbackR / fallbackCount)
          finalG = Math.round(fallbackG / fallbackCount)
          finalB = Math.round(fallbackB / fallbackCount)
        } else {
          resolve(defaultColor)
          return
        }

        const toHex = (c) => Math.min(255, Math.max(0, c)).toString(16).padStart(2, '0')
        resolve(`#${toHex(finalR)}${toHex(finalG)}${toHex(finalB)}`)
      } catch (err) {
        resolve(defaultColor)
      }
    }

    img.onerror = () => {
      resolve(defaultColor)
    }

    img.src = imageUrl
  })

export default extractDominantColor
